import FulfilmentsUtil from "../../utils/fulfilments-util";
import { R, asFraction, toJSON, toPercentNumber } from "../../utils/rational";
import { STATUS_NREL } from "../../utils/excel-constants";
const util = new FulfilmentsUtil();

export default {
  methods: {
    findParentArrayAndIndex(items, id, parentNode = null) {
      if (!Array.isArray(items)) return null;
      for (let i = 0; i < items.length; i++) {
        const node = items[i];
        if (node.id === id) {
          return { parentArray: items, index: i, node, parentNode };
        }
        if (node.children && node.children.length) {
          const res = this.findParentArrayAndIndex(node.children, id, node);
          if (res) return res;
        }
      }
      return null;
    },

    createNewRowFromSelected() {
      if (!this.isAuthenticated) {
        return this.$store.dispatch("addToast", {
          severity: "info",
          life: 3000,
          summary: "Not signed in",
          detail: "You need to sign in to create subproject",
        });
      }

      if (this.selectedRows.length === 0) return;

      const activeLevel = (this.getActiveDeliverables && this.getActiveDeliverables()) || this.deliverables;
      const locations = this.selectedRows
        .map((id) => this.findParentArrayAndIndex(activeLevel, id))
        .filter((loc) => loc && loc.parentArray);

      if (locations.length === 0) return;

      const targetParent = locations[0].parentArray;
      const sameParent = locations.every((loc) => loc.parentArray === targetParent);
      if (!sameParent) {
        return this.$store.dispatch("addToast", {
          severity: "warn",
          life: 3000,
          summary: "Select same level",
          detail: "Please select rows from the same level to group.",
        });
      }

      const ordered = [...locations].sort((a, b) => a.index - b.index);
      const selectedItems = ordered.map((loc) => loc.parentArray[loc.index]);
      ordered
        .map((loc) => loc.index)
        .sort((a, b) => b - a)
        .forEach((idx) => targetParent.splice(idx, 1));

      const newRow = {
        description: "Subproject",
        fulfillment: toJSON(R(0)),
        id: "",
        responsible: "",
        showEditDialog: false,
        weight: toJSON(R(0)),
        isChild: true,
        children: selectedItems,
        isGrouped: true,
        expanded: true,
      };

      const weightSum = selectedItems.reduce((sum, item) => sum.add(asFraction(item.weight)), R(0));
      newRow.weight = toJSON(weightSum);

      const insertIndex = Math.min(...ordered.map((loc) => loc.index));
      targetParent.splice(insertIndex, 0, newRow);

      this.selectedRows = [];
      this.toolboxVisible = false;

      this.renumberDeliverables(this.deliverables);
      this.syncParentIds && this.syncParentIds(this.deliverables);
      this.calculateStepsFulfillmentForNewRow(newRow);
      util.recalculateAllFulfillments(this.deliverables, this.steps);
      this.$store.dispatch("setDeliverables", this.deliverables);
    },

    ungroupSelectedChildren() {
      if (this.selectedRows.length === 0) return;

      const activeParent = this.navigationStack && this.navigationStack.length > 0 ? this.navigationStack.slice(-1)[0] : null;

      const targets = this.selectedRows
        .map((id) => this.findParentArrayAndIndex(this.deliverables, id))
        .filter((loc) => loc && loc.node)
        .filter((loc) => {
          // keep only rows that belong to the current level we are viewing
          const parentId = loc.parentNode ? loc.parentNode.id : undefined;
          if (!activeParent) return parentId === undefined;
          return parentId === activeParent.id;
        })
        .sort((a, b) => b.index - a.index);

      if (targets.length === 0) return;

      targets.forEach((loc) => {
        const { parentArray, index, node, parentNode } = loc;
        const children = Array.isArray(node.children) ? node.children : [];

        if (children.length > 0) {
          children.forEach((child) => {
            const hasNestedChildren = Array.isArray(child.children) && child.children.length > 0;
            child.isGrouped = hasNestedChildren;
            child.isChild = hasNestedChildren;
          });
          parentArray.splice(index, 1, ...children);
        } else if (parentNode) {
          // Promote a single child up one level (e.g., 5.1 -> alongside 5)
          const parentLoc = this.findParentArrayAndIndex(this.deliverables, parentNode.id);
          if (!parentLoc || !parentLoc.parentArray) return;

          const [child] = parentArray.splice(index, 1);
          if (!child) return;
          const hasNestedChildren = Array.isArray(child.children) && child.children.length > 0;
          child.isGrouped = hasNestedChildren;
          child.isChild = hasNestedChildren;

          parentLoc.parentArray.splice(parentLoc.index + 1, 0, child);

          // If parent has no more children, clear its grouping flags
          const remainingChildren = Array.isArray(parentNode.children) ? parentNode.children.length : 0;
          if (remainingChildren === 0) {
            parentLoc.parentArray.splice(parentLoc.index, 1);
          }
        }
      });

      this.selectedRows = [];
      this.pruneEmptyGroups && this.pruneEmptyGroups(this.deliverables);
      this.syncParentIds && this.syncParentIds(this.deliverables);
      this.renumberDeliverables(this.deliverables);
      util.recalculateAllFulfillments(this.deliverables, this.steps);
      this.$store.dispatch("setDeliverables", this.deliverables);
    },

    calculateStepsFulfillmentForNewRow(row) {
      if (!row || !Array.isArray(row.children) || row.children.length === 0 || !this.steps.length) return;

      const leaves = util.collectLeaves(row).filter((leaf) => leaf !== row);
      let sumAll = R(0);

      this.steps.forEach((step) => {
        let totalWeights = R(0);
        let wipCount = R(0);
        let closedCount = R(0);

        leaves.forEach((item) => {
          const weight = asFraction(item.weight);
          if (item[step.identity] !== "n.rel.") {
            totalWeights = totalWeights.add(weight);
          }
          closedCount = item[step.identity] === "closed" ? closedCount.add(weight) : closedCount;
          wipCount = item[step.identity] === "wip" ? wipCount.add(weight.mul(R(1, 2))) : wipCount;
        });

        row[step.identity] =
          totalWeights.equals(0) ? STATUS_NREL : toPercentNumber(closedCount.add(wipCount).div(totalWeights));
      });

      if (this.steps.length > 0) {
        let totalWeights = R(0);
        leaves.forEach((item) => {
          if (item.fulfillment !== "n.rel.") {
            const weight = asFraction(item.weight);
            const fulfillment = asFraction(item.fulfillment);
            totalWeights = totalWeights.add(weight);
            sumAll = sumAll.add(fulfillment.mul(weight));
          }
        });
        row.fulfillment = totalWeights.equals(0) ? toJSON(R(0)) : toJSON(sumAll.div(totalWeights));
      }
    },
  },
};
